import type { ReactNode } from "react";
import { Link } from "wouter";
import { ArrowLeft } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { AppTopBar } from "@/components/home/AppTopBar";

/**
 * Chrome for NON-TAB pages (Profile, admin, platform console) so they render
 * EXACTLY like the tabbed screens: the same AppTopBar brand row and the same
 * centered `max-w-lg` column as TabScaffold. These pages are mounted inside
 * `tabbed()` (TabShell), so they also get the bottom tab bar + capture FAB +
 * auth gate for free.
 *
 * This replaced the old `AppHeader` chrome on these routes — the dual header
 * system (full-width brand bar, streak chip, its own avatar menu, no bottom
 * nav) was why Profile/admin screens read as "a different app" (Web v1
 * layout-consistency finding).
 */
export function PageChrome({
  title,
  subtitle,
  backHref,
  backLabel,
  headerRight,
  children,
}: {
  title?: string;
  subtitle?: ReactNode;
  /** Optional back link rendered above the title (e.g. "/" or "/more"). */
  backHref?: string;
  backLabel?: string;
  headerRight?: ReactNode;
  children: ReactNode;
}) {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  return (
    <div className="text-foreground">
      <div data-testid="tab-topbar">
        <AppTopBar />
      </div>
      <header className="mx-auto w-full max-w-lg px-4 pt-3 pb-2">
        {backHref ? (
          <Link
            href={backHref}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-1"
          >
            <ArrowLeft className="h-4 w-4" />
            {backLabel ?? (isAr ? "رجوع" : "Back")}
          </Link>
        ) : null}
        {title ? (
          <div className="flex items-end justify-between gap-3">
            <div className="min-w-0">
              <h1 className="font-display text-[26px] leading-tight sm:text-3xl tracking-[-0.01em]">
                {title}
              </h1>
              {subtitle ? (
                <div className="text-sm text-muted-foreground mt-0.5">{subtitle}</div>
              ) : null}
            </div>
            {headerRight ? <div className="shrink-0 pb-0.5">{headerRight}</div> : null}
          </div>
        ) : null}
      </header>
      <main className="mx-auto w-full max-w-lg px-4 pb-2 space-y-5">{children}</main>
    </div>
  );
}
